// Wire format of spec section 4.1. Big-endian; bytes 2-3 are flags, written as zero and ignored on read.
export const VERSION = 1;
export const TYPE_PROBE = 1;
export const TYPE_ECHO = 2;
export const TYPE_LOAD_REPORT = 3;
export const PROBE_SIZE = 20;
export const ECHO_SIZE = 40;
export const LOAD_REPORT_SIZE = 16;

export interface Probe {
    sessionId: number;
    seq: number;
    clientSendUs: number;
}

export interface Echo {
    sessionId: number;
    seq: number;
    clientSendUs: number;
    serverRecvUs: number;
    ackHi: number;
    ackBits: number;
    recvCount: number;
}

export interface LoadReport {
    sessionId: number;
    bytes: number;
    messages: number;
}

function frame(size: number, type: number): [ArrayBuffer, DataView] {
    const buf = new ArrayBuffer(size);
    const view = new DataView(buf);
    view.setUint8(0, VERSION);
    view.setUint8(1, type);
    return [buf, view];
}

function view(buf: ArrayBuffer, size: number, type: number): DataView | null {
    if (buf.byteLength !== size) return null;
    const v = new DataView(buf);
    return v.getUint8(0) === VERSION && v.getUint8(1) === type ? v : null;
}

// Microsecond timestamps fit a double exactly up to 2^53 µs (285 years).
const setU64 = (v: DataView, at: number, value: number) => v.setBigUint64(at, BigInt(Math.max(0, Math.round(value))));
const getU64 = (v: DataView, at: number) => Number(v.getBigUint64(at));

export function encodeProbe(p: Probe): ArrayBuffer {
    const [buf, v] = frame(PROBE_SIZE, TYPE_PROBE);
    v.setUint32(4, p.sessionId >>> 0);
    v.setUint32(8, p.seq >>> 0);
    setU64(v, 12, p.clientSendUs);
    return buf;
}

export function decodeProbe(buf: ArrayBuffer): Probe | null {
    const v = view(buf, PROBE_SIZE, TYPE_PROBE);
    return v && { sessionId: v.getUint32(4), seq: v.getUint32(8), clientSendUs: getU64(v, 12) };
}

export function encodeEcho(e: Echo): ArrayBuffer {
    const [buf, v] = frame(ECHO_SIZE, TYPE_ECHO);
    v.setUint32(4, e.sessionId >>> 0);
    v.setUint32(8, e.seq >>> 0);
    setU64(v, 12, e.clientSendUs);
    setU64(v, 20, e.serverRecvUs);
    v.setUint32(28, e.ackHi >>> 0);
    v.setUint32(32, e.ackBits >>> 0);
    v.setUint32(36, e.recvCount >>> 0);
    return buf;
}

export function decodeEcho(buf: ArrayBuffer): Echo | null {
    const v = view(buf, ECHO_SIZE, TYPE_ECHO);
    return (
        v && {
            sessionId: v.getUint32(4),
            seq: v.getUint32(8),
            clientSendUs: getU64(v, 12),
            serverRecvUs: getU64(v, 20),
            ackHi: v.getUint32(28),
            ackBits: v.getUint32(32),
            recvCount: v.getUint32(36)
        }
    );
}

export function encodeLoadReport(r: LoadReport): ArrayBuffer {
    const [buf, v] = frame(LOAD_REPORT_SIZE, TYPE_LOAD_REPORT);
    v.setUint32(4, r.sessionId >>> 0);
    v.setUint32(8, r.bytes >>> 0);
    v.setUint32(12, r.messages >>> 0);
    return buf;
}

export function decodeLoadReport(buf: ArrayBuffer): LoadReport | null {
    const v = view(buf, LOAD_REPORT_SIZE, TYPE_LOAD_REPORT);
    return v && { sessionId: v.getUint32(4), bytes: v.getUint32(8), messages: v.getUint32(12) };
}
